<template>
  <div class="col chat-right-aside topix-support-chat">
    <div v-if="!ticket" class="chat">
      <div class="call-content">
        <div class="text-center text-muted py-5">
          <vue-feather type="message-circle" size="42" class="mb-3" />
          <p class="mb-0">Selecciona un ticket de soporte para ver la conversación.</p>
        </div>
      </div>
    </div>

    <div v-else class="chat">
      <div class="d-flex chat-header align-items-center">
        <div class="topix-chat-header-photo-wrap">
          <img
            v-if="authorPhoto && !photoError"
            class="topix-chat-header-photo"
            :src="authorPhoto"
            alt=""
            @error="photoError = true"
          />
          <div v-else class="topix-chat-header-photo topix-chat-header-photo--placeholder">
            {{ authorInitials }}
          </div>
        </div>
        <div class="flex-grow-1 ms-3 min-w-0">
          <div class="name fw-semibold mb-0">{{ ticket.authorName || 'Usuario' }}</div>
          <div class="status digits small text-muted text-truncate">
            {{ ticket.authorEmail || '—' }} · {{ ticket.categoryLabel }}
          </div>
        </div>
        <div class="ms-2 flex-shrink-0">
          <select
            class="form-select form-select-sm rounded-pill"
            :value="ticket.status"
            :disabled="updatingStatus"
            @change="onStatusChange($event.target.value)"
          >
            <option value="IN_PROGRESS">En proceso</option>
            <option value="OPEN">Sin resolver</option>
            <option value="RESOLVED">Resuelto</option>
          </select>
        </div>
      </div>

      <div ref="historyBox" class="chat-history chat-msg-box custom-scrollbar topix-chat-timeline">
        <ul class="topix-chat-messages">
          <li
            v-for="entry in timeline"
            :key="entry.id"
            class="topix-chat-row"
            :class="entry.isAdmin ? 'topix-chat-row--admin' : 'topix-chat-row--user'"
          >
            <div
              class="topix-chat-bubble"
              :class="entry.isAdmin ? 'topix-chat-bubble--admin' : 'topix-chat-bubble--user'"
            >
              <div class="topix-chat-time">{{ formatDateTime(entry.createdAt) }}</div>
              <strong v-if="entry.isInitial" class="d-block mb-1">Reporte inicial</strong>
              <div class="topix-chat-body">{{ entry.body }}</div>
              <img
                v-if="entry.imageUrl"
                :src="entry.imageUrl"
                alt="Adjunto"
                class="topix-chat-attachment mt-2 rounded"
              />
            </div>
          </li>
        </ul>
      </div>

      <div class="chat-message clearfix">
        <div v-if="actionError" class="alert alert-danger py-2 small mx-3">{{ actionError }}</div>
        <div v-if="pendingImageName" class="small text-muted px-3 mb-2">
          Imagen lista: {{ pendingImageName }}
          <button class="btn btn-link btn-sm p-0 ms-1" type="button" @click="clearImage">Quitar</button>
        </div>
        <div class="row">
          <div class="col-xl-12 d-flex">
            <div class="input-group text-box flex-grow-1">
              <input
                ref="fileInput"
                type="file"
                accept="image/*"
                class="d-none"
                @change="onFilePicked"
              />
              <button
                class="btn btn-outline-secondary"
                type="button"
                :disabled="!ticket.canReply || sending"
                @click="$refs.fileInput.click()"
              >
                <vue-feather type="image" size="18" />
              </button>
              <input
                v-model.trim="draft"
                class="form-control input-txt-bx"
                type="text"
                placeholder="Escribe una respuesta..."
                :disabled="!ticket.canReply || sending"
                @keyup.enter="sendMessage"
              />
              <button
                class="btn btn-primary input-group-text"
                type="button"
                :disabled="!ticket.canReply || sending || (!draft && !pendingFile)"
                @click="sendMessage"
              >
                {{ sending ? 'Enviando...' : 'Enviar' }}
              </button>
            </div>
          </div>
        </div>
        <p v-if="!ticket.canReply" class="small text-muted px-3 mb-0 mt-2">
          Este ticket está resuelto y no admite más respuestas.
        </p>
      </div>
    </div>
  </div>
</template>

<script>
import {
  postSupportTicketMessage,
  updateSupportTicketStatus,
  uploadSupportImage,
} from '@/services/supportTicketsApi';

function parseTime(iso) {
  if (!iso) return 0;
  const t = new Date(iso).getTime();
  return Number.isNaN(t) ? 0 : t;
}

export default {
  name: 'SupportTicketChat',
  props: {
    ticket: { type: Object, default: null },
  },
  emits: ['updated'],
  data() {
    return {
      draft: '',
      sending: false,
      updatingStatus: false,
      actionError: '',
      photoError: false,
      pendingFile: null,
    };
  },
  computed: {
    pendingImageName() {
      return this.pendingFile?.name || '';
    },
    authorPhoto() {
      const author = this.ticket?.client || this.ticket?.driver;
      return author?.profilePhotoUrl || this.ticket?.authorPhotoUrl || '';
    },
    authorInitials() {
      const name = this.ticket?.authorName || '?';
      return String(name)
        .trim()
        .split(/\s+/)
        .filter(Boolean)
        .slice(0, 2)
        .map((p) => p[0]?.toUpperCase() || '')
        .join('') || '?';
    },
    timeline() {
      if (!this.ticket) return [];
      const items = [
        {
          id: `initial-${this.ticket.id}`,
          isAdmin: false,
          isInitial: true,
          body: this.ticket.description || '',
          imageUrl: this.ticket.imageUrl || null,
          createdAt: this.ticket.occurredAt || this.ticket.createdAt,
        },
      ];
      const messages = Array.isArray(this.ticket.messages) ? this.ticket.messages : [];
      messages.forEach((msg) => {
        items.push({
          id: msg.id,
          isAdmin: msg.senderType === 'ADMIN',
          isInitial: false,
          body: msg.body,
          imageUrl: msg.imageUrl || null,
          createdAt: msg.createdAt || this.ticket.createdAt,
        });
      });
      return items.sort((a, b) => parseTime(a.createdAt) - parseTime(b.createdAt));
    },
  },
  watch: {
    ticket: {
      deep: true,
      handler() {
        this.photoError = false;
        this.actionError = '';
        this.$nextTick(() => this.scrollToBottom());
      },
    },
    timeline: {
      deep: true,
      handler() {
        this.$nextTick(() => this.scrollToBottom());
      },
    },
  },
  methods: {
    formatDateTime(iso) {
      if (!iso) return '—';
      const d = new Date(iso);
      if (Number.isNaN(d.getTime())) return '—';
      return d.toLocaleString('es-CL');
    },
    scrollToBottom() {
      const box = this.$refs.historyBox;
      if (box) box.scrollTop = box.scrollHeight;
    },
    onFilePicked(event) {
      const file = event.target.files?.[0];
      this.pendingFile = file || null;
    },
    clearImage() {
      this.pendingFile = null;
      if (this.$refs.fileInput) this.$refs.fileInput.value = '';
    },
    async sendMessage() {
      if (!this.ticket?.id || this.sending) return;
      if (!this.draft && !this.pendingFile) return;
      this.sending = true;
      this.actionError = '';
      try {
        let imageUrl;
        if (this.pendingFile) {
          imageUrl = await uploadSupportImage(this.pendingFile);
        }
        const updated = await postSupportTicketMessage(
          this.ticket.id,
          this.draft || 'Imagen adjunta',
          imageUrl,
        );
        this.draft = '';
        this.clearImage();
        this.$emit('updated', updated);
      } catch (e) {
        this.actionError = e.message || 'No se pudo enviar el mensaje';
      } finally {
        this.sending = false;
      }
    },
    async onStatusChange(status) {
      if (!this.ticket?.id || status === this.ticket.status || this.updatingStatus) return;
      this.updatingStatus = true;
      this.actionError = '';
      try {
        const updated = await updateSupportTicketStatus(this.ticket.id, status);
        this.$emit('updated', updated);
      } catch (e) {
        this.actionError = e.message || 'No se pudo actualizar el estado';
      } finally {
        this.updatingStatus = false;
      }
    },
  },
};
</script>

<style scoped>
.topix-chat-header-photo-wrap {
  flex-shrink: 0;
}

.topix-chat-header-photo {
  width: 50px;
  height: 50px;
  border-radius: 50%;
  object-fit: cover;
  display: block;
}

.topix-chat-header-photo--placeholder {
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, #273278, #4a5aa8);
  color: #fff;
  font-weight: 700;
}

.topix-chat-messages {
  list-style: none;
  padding: 0;
  margin: 0;
  display: flex;
  flex-direction: column;
  gap: 14px;
}

.topix-chat-row {
  display: flex;
  width: 100%;
}

.topix-chat-row--user {
  justify-content: flex-start;
}

.topix-chat-row--admin {
  justify-content: flex-end;
}

.topix-chat-bubble {
  max-width: min(72%, 520px);
  padding: 12px 16px;
  border-radius: 14px;
  font-size: 14px;
  line-height: 1.5;
  word-break: break-word;
}

.topix-chat-bubble--user {
  background: #f1f3f5;
  border: 1px solid #e9ecef;
  border-bottom-left-radius: 4px;
}

.topix-chat-bubble--admin {
  background: #e8ebf8;
  border: 1px solid rgba(39, 50, 120, 0.25);
  border-bottom-right-radius: 4px;
}

.topix-chat-time {
  font-size: 11px;
  color: #6c757d;
  margin-bottom: 6px;
  font-weight: 600;
}

.topix-chat-body {
  white-space: pre-wrap;
}

.topix-chat-attachment {
  display: block;
  max-width: 220px;
  max-height: 180px;
  object-fit: cover;
}

.topix-chat-timeline {
  min-height: 360px;
  max-height: 520px;
  overflow-y: auto;
  padding: 16px;
}
</style>
